import React, { useRef, useEffect } from "react";
import EnrolledThroughPill from "../molecules/EnrolledThroughPill";
import type { EnrolledThroughType } from "../molecules/EnrolledThroughPill";
import EnrolmentStatusPill from "../molecules/EnrolmentStatusPill";
import type { EnrolmentStatusType } from "../molecules/EnrolmentStatusPill";
import EnrolmentTableActions from "../molecules/EnrolmentTableActions";
import TablePaginationFooter from "../../ui/molecules/TablePaginationFooter";
import { formatDate } from "../../../utils/formatUtils";
import type { EnrolmentItem } from "../../../api/academyEnrolments/enrolmentsService";

/** Derive display status from isActive flag and enrollment date range. */
function deriveStatus(enrolment: EnrolmentItem): EnrolmentStatusType {
  if (!enrolment.isActive) return "expired";
  const now = new Date();
  if (enrolment.startDate && now < new Date(enrolment.startDate)) return "upcoming";
  if (enrolment.endDate && now > new Date(enrolment.endDate)) return "expired";
  return "active";
}

/** Normalise known API enrolledThrough values to pill keys without collapsing unknown labels. */
function normaliseEnrolledThrough(value: string): EnrolledThroughType {
  const lower = (value ?? "").trim().toLowerCase();
  if (lower === "partner app - offline") return "partner_app_offline";
  if (lower === "online" || lower === "excel" || lower === "offline") return lower;
  return value;
}

interface EnrolmentsTableProps {
  enrolments: EnrolmentItem[];
  loading?: boolean;
  error?: string | null;
  page: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewEnrolment?: (enrolment: EnrolmentItem) => void;
  onEditEnrolment?: (enrolment: EnrolmentItem) => void;
  onDeleteEnrolment?: (enrolment: EnrolmentItem) => void;
  onToggleActive?: (enrolment: EnrolmentItem) => void;
  selectedIds?: Set<string>;
  onToggleSelectOne?: (id: string) => void;
  onToggleSelectAllOnPage?: (rows: EnrolmentItem[]) => void;
}

const EnrolmentsTable: React.FC<EnrolmentsTableProps> = ({
  enrolments,
  loading = false,
  error,
  page,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onViewEnrolment,
  onEditEnrolment,
  onDeleteEnrolment,
  onToggleActive,
  selectedIds = new Set(),
  onToggleSelectOne,
  onToggleSelectAllOnPage,
}) => {
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const deletableOnPage = enrolments.filter((e) => {
    const lower = (e.enrolledThrough ?? "").trim().toLowerCase();
    return lower === "excel" || lower === "offline" || lower === "partner app - offline";
  });

  const selectedOnPage = deletableOnPage.filter((e) => selectedIds.has(e._id));
  const allDeletableSelected =
    deletableOnPage.length > 0 && selectedOnPage.length === deletableOnPage.length;
  const someDeletableSelected =
    selectedOnPage.length > 0 && selectedOnPage.length < deletableOnPage.length;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someDeletableSelected;
    }
  }, [someDeletableSelected]);

  if (loading) {
    return (
      <div className="academy-enrolments-table-wrapper">
        <div className="text-center py-5 text-muted">Loading enrolments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="academy-enrolments-table-wrapper">
        <div className="text-center py-5 text-danger">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="academy-enrolments-table-wrapper">
        <div className="table-responsive">
          <table className="academy-enrolments-table table align-middle table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th scope="col" style={{ width: 40 }}>
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allDeletableSelected}
                    disabled={deletableOnPage.length === 0}
                    onChange={() => onToggleSelectAllOnPage?.(enrolments)}
                    title="Select all deletable rows on this page"
                  />
                </th>
                <th scope="col">Player Name</th>
                <th scope="col">Phone Number</th>
                <th scope="col">Academy</th>
                <th scope="col">Centre</th>
                <th scope="col">Sport</th>
                <th scope="col">Course / Batch</th>
                <th scope="col">Start Date</th>
                <th scope="col">End Date</th>
                <th scope="col">Paid At</th>
                <th scope="col">Amount</th>
                <th scope="col">Enrolled Through</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrolments.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center py-4 text-muted">
                    No enrolments found
                  </td>
                </tr>
              ) : (
                enrolments.map((enrolment) => {
                  const playerName = enrolment.player?.fullName || "—";
                  const countryCode = enrolment.user?.countryCode
                    ? `+${enrolment.user.countryCode} `
                    : "";
                  const phoneNumber = enrolment.user?.phoneNumber
                    ? `${countryCode}${enrolment.user.phoneNumber}`
                    : "—";
                  const academyName =
                    enrolment.academy?.basicDetails?.academyName || "—";
                  const centre =
                    enrolment.coachingCenter?.basicDetails?.location ||
                    enrolment.location ||
                    "—";
                  const sport = enrolment.sports || "—";
                  const courseBatch =
                    [enrolment.courseName, enrolment.batchName]
                      .filter(Boolean)
                      .join(" / ") || "—";
                  const amount =
                    enrolment.totalFeeInfo?.totalFeeAfterGST != null
                      ? `₹${enrolment.totalFeeInfo.totalFeeAfterGST.toLocaleString("en-IN")}`
                      : "—";
                  const enrolledThrough = normaliseEnrolledThrough(
                    enrolment.enrolledThrough
                  );
                  const status = deriveStatus(enrolment);

                  const isDeletable =
                    enrolledThrough === "excel" ||
                    enrolledThrough === "offline" ||
                    enrolledThrough === "partner_app_offline";

                  return (
                    <tr key={enrolment._id}>
                      <td style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(enrolment._id)}
                          disabled={!isDeletable}
                          onChange={() => onToggleSelectOne?.(enrolment._id)}
                          title={!isDeletable ? "Online enrolments cannot be deleted" : undefined}
                        />
                      </td>
                      <td>
                        <div className="fw-medium">{playerName}</div>
                      </td>
                      <td>{phoneNumber}</td>
                      <td>{academyName}</td>
                      <td>{centre}</td>
                      <td>{sport}</td>
                      <td>{courseBatch}</td>
                      <td>
                        {enrolment.startDate ? formatDate(enrolment.startDate) : "—"}
                      </td>
                      <td>
                        {enrolment.endDate ? formatDate(enrolment.endDate) : "—"}
                      </td>
                      <td>
                        {enrolment.paidAt ? formatDate(enrolment.paidAt) : "—"}
                      </td>
                      <td>{amount}</td>
                      <td>
                        <EnrolledThroughPill enrolledThrough={enrolledThrough} />
                      </td>
                      <td>
                        <EnrolmentStatusPill status={status} />
                      </td>
                      <td>
                        <EnrolmentTableActions
                          enrolledThrough={enrolledThrough}
                          isActive={enrolment.isActive}
                          onView={
                            onViewEnrolment
                              ? () => onViewEnrolment(enrolment)
                              : undefined
                          }
                          onEdit={
                            onEditEnrolment
                              ? () => onEditEnrolment(enrolment)
                              : undefined
                          }
                          onDelete={
                            onDeleteEnrolment
                              ? () => onDeleteEnrolment(enrolment)
                              : undefined
                          }
                          onToggleActive={
                            onToggleActive
                              ? () => onToggleActive(enrolment)
                              : undefined
                          }
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <TablePaginationFooter
        page={page}
        totalPages={Math.max(1, totalPages)}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};

export default EnrolmentsTable;
